import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  useCluster,
  useHistory,
  useQuestion,
  useSplitDistribution,
  type QuestionSummary,
} from "@/api/queries";
import { useCan } from "@/auth/member";
import { FollowUpSection } from "@/components/follow-up-section";
import { QuestionEditForm } from "@/components/question-edit-form";
import { QuestionStatusActions } from "@/components/question-status-actions";
import { SpeechChip } from "@/components/speech-chip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, Empty, ErrorNote } from "@/components/ui/panel";
import { cn, decisionSummary, formatDate, formatScore } from "@/lib/utils";

/**
 * A question's full record plus its status_transitions trail (DESIGN §7.5) — every
 * pipeline stage and every human decision, with the note that carries a rejection or
 * quarantine reason. Shared by Review's detail pane and Questions' history drawer so
 * there is one rendering of "what a question looks like," not two that can drift
 * (ARCHITECTURE §10 calls that out as the risk of a second review-adjacent view).
 *
 * `showStatusActions` is Questions' alone: Review already has its own approve/reject/
 * requeue bar (and the j/k/a/r/e shortcuts) built for its pending_review/stale queue,
 * so repeating them here would just be a second, competing set of the same buttons.
 * Questions is the only screen that can still reach a question once it has left that
 * queue — exactly where a decision (a mistaken reject, most often) needs correcting.
 *
 * `onOpenQuestion`, when given, lets the cluster section (below) re-point this same
 * pane at another member of the cluster instead of only linking to the Clusters page
 * — the whole reason that section exists (see `ClusterSection`).
 */
export function QuestionDetail({
  id,
  showStatusActions = false,
  onOpenQuestion,
}: {
  id: number;
  showStatusActions?: boolean;
  onOpenQuestion?: ((id: number) => void) | undefined;
}) {
  const { data: q, isLoading, error } = useQuestion(id);
  const { data: history } = useHistory(id);
  const { data: distribution } = useSplitDistribution();
  const speechLimit = distribution?.limit_seconds ?? 6;
  const [editing, setEditing] = useState(false);
  const canEdit = useCan("edit:question_details") || useCan("review") || useCan("curate");

  if (isLoading) return <Empty>loading…</Empty>;
  if (error) return <ErrorNote error={error} />;
  if (!q) return <Empty>Not found.</Empty>;

  // §7.2 quarantine reasons arrive as the note on the last transition, and it is the
  // single most useful thing on this screen — it says why a human is being asked.
  const lastNote = history?.at(-1)?.note;

  return (
    <div className="space-y-4 p-4">
      {lastNote && <Callout>{lastNote}</Callout>}
      {(q.jds ?? []).map((jd) => (
        <Callout key={jd.jd_id} tone="info">
          <strong>From a job description:</strong>{" "}
          <Link to={`/jds/${jd.jd_id}`} className="text-bank-accent-ink hover:underline">
            {jd.title}
          </Link>
          . Added by {jd.added_by ?? "—"} on {formatDate(jd.added_at)}
          {jd.after_publish ? ", after the JD was published" : ""}.{" "}
          {q.status === "approved" ? "Serves" : "Suggested for"}:{" "}
          {q.scope === "jd_only" ? "this JD only" : "the whole bank"}.
        </Callout>
      ))}
      {q.ingested_by === "ai-generator" && (
        <Callout>
          <strong>AI-generated.</strong> The model wrote this question, and enrichment then wrote its answer
          and rubric — no document backs either, so check the answer, not just the wording.
        </Callout>
      )}

      {editing ? (
        <QuestionEditForm q={q} onCancel={() => setEditing(false)} onSaved={() => setEditing(false)} />
      ) : (
        <>
          <div className="flex items-start justify-between gap-3">
            <p className="question-text max-w-[62ch] text-[15px]">{q.question_text}</p>
            {canEdit && (
              <Button size="sm" className="shrink-0" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
          </div>

          <Field label="Signature — this is what gets embedded (§2.1)">
            <p className="border-bank-border bg-bank-surface-2 rounded-md border px-2.5 py-2 font-mono text-xs">
              {q.concept_signature ?? "—"}
            </p>
          </Field>
          {q.model_answer && <Field label="Model answer">{q.model_answer}</Field>}
          {q.rubric.length > 0 && (
            <Field label="Rubric">
              <ul className="list-disc space-y-0.5 pl-4">
                {q.rubric.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </Field>
          )}

          {q.validation_warnings.length > 0 && <Callout>{q.validation_warnings.join("; ")}</Callout>}

          <dl className="border-bank-border grid grid-cols-2 gap-x-4 gap-y-2.5 border-t pt-3 text-sm sm:grid-cols-3">
            <Meta label="status">
              <Badge value={q.status} />
            </Meta>
            <Meta label="stack / concept">
              {q.stack_path ?? "—"} · {q.primary_concept ?? "—"}
            </Meta>
            <Meta label="type">{q.question_type ?? "—"}</Meta>
            <Meta label="difficulty" numeric>
              {q.difficulty_score ?? "—"}
            </Meta>
            <Meta label="quality" numeric>
              {formatScore(q.quality_score)}
            </Meta>
            <Meta label="entities">{q.entities.join(", ") || "—"}</Meta>
            <Meta label="versions" numeric>
              {q.version_min ?? "—"} … {q.version_max ?? "—"}
            </Meta>
            <Meta label="cluster">
              {q.canonical_id
                ? `variant of #${q.canonical_id}`
                : (q.cluster_size ?? 1) > 1
                  ? "canonical"
                  : "—"}
            </Meta>
            <Meta label="spoken">
              <SpeechChip seconds={q.speech_seconds} limit={speechLimit} />
            </Meta>
          </dl>

          <ClusterSection q={q} onOpenQuestion={onOpenQuestion} />
          <FollowUpSection q={q} onOpenQuestion={onOpenQuestion} />

          {showStatusActions && <QuestionStatusActions q={q} />}
        </>
      )}

      <p className="text-bank-muted border-bank-border border-t pt-3 font-mono text-[11px] leading-relaxed break-words">
        {q.source_doc ?? "—"} @ {q.source_offset ?? "?"} · extraction v{q.extraction_version ?? "?"} ·
        enrichment v{q.enrichment_version ?? "?"} ({(q.enrichment_prompt_hash ?? "").slice(0, 8)}) · embedding{" "}
        {q.embedding_model ?? "none"}
      </p>

      {history && history.length > 0 && (
        <details className="text-sm">
          <summary className="text-bank-muted cursor-pointer font-mono text-[11px]">
            history — {history.length} transitions
          </summary>
          <ul className="mt-2 space-y-1">
            {history.map((t) => (
              <li key={t.id} className="text-bank-muted font-mono text-[11px]">
                {formatDate(t.created_at)} · {t.from_status ?? "∅"} → {t.to_status} · {t.actor}
                {t.note ? ` · ${t.note}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-bank-muted mb-1 font-mono text-[10.5px] tracking-[0.07em] uppercase">{label}</p>
      <div className="question-text text-sm">{children}</div>
    </div>
  );
}

function Meta({
  label,
  numeric = false,
  children,
}: {
  label: string;
  numeric?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-bank-muted mb-0.5 font-mono text-[10.5px] tracking-[0.07em] uppercase">{label}</dt>
      <dd className={cn("truncate text-[13px]", numeric && "font-mono tabular-nums")}>{children}</dd>
    </div>
  );
}

/**
 * The "cluster" `Meta` row above only ever said *that* a question is linked — canonical,
 * or a variant naming the canonical's id — with no way to act on it short of finding
 * the same cluster on the Clusters page by hand. This fetches that one cluster
 * (`useCluster` hits the same `GET /clusters/{canonical_id}` Clusters itself pages
 * through) and lists every other member right here, each one a click away via
 * `onOpenQuestion` — so following a duplicate chain stays inside this pane instead of
 * a round trip through nav.
 */
function ClusterSection({
  q,
  onOpenQuestion,
}: {
  q: { id: number; canonical_id: number | null; cluster_size: number | null };
  onOpenQuestion?: ((id: number) => void) | undefined;
}) {
  // A variant's cluster is its canonical's; a canonical with variants is its own.
  const canonicalId = q.canonical_id ?? ((q.cluster_size ?? 1) > 1 ? q.id : null);
  const { data: cluster, isLoading, error } = useCluster(canonicalId);

  if (canonicalId === null || error) return null;

  return (
    <div className="border-bank-border border-t pt-3">
      <p className="text-bank-muted mb-2 font-mono text-[10.5px] tracking-[0.07em] uppercase">
        Cluster
        {cluster
          ? ` — ${cluster.member_count} members · coherence ${formatScore(cluster.coherence_score)}`
          : ""}
      </p>
      {isLoading && <p className="text-bank-muted text-sm">loading…</p>}
      {cluster && (
        <ul className="space-y-1.5">
          {cluster.canonical.id !== q.id && (
            <ClusterMemberRow question={cluster.canonical} isCanonical onOpen={onOpenQuestion} />
          )}
          {cluster.variants
            .filter((v) => v.question.id !== q.id)
            .map(({ question, decision }) => (
              <ClusterMemberRow
                key={question.id}
                question={question}
                decision={decision}
                onOpen={onOpenQuestion}
              />
            ))}
        </ul>
      )}
    </div>
  );
}

function ClusterMemberRow({
  question,
  decision = null,
  isCanonical = false,
  onOpen,
}: {
  question: QuestionSummary;
  decision?: Parameters<typeof decisionSummary>[0];
  isCanonical?: boolean;
  onOpen?: ((id: number) => void) | undefined;
}) {
  const clickable = Boolean(onOpen);
  return (
    <li
      onClick={clickable ? () => onOpen!(question.id) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen!(question.id);
              }
            }
          : undefined
      }
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? `Open question ${question.id}` : undefined}
      className={cn(
        "border-bank-border grid grid-cols-[1fr_auto] items-center gap-2 rounded-md border px-2.5 py-2",
        clickable &&
          "hover:bg-bank-surface-2 focus-visible:outline-bank-accent cursor-pointer transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2",
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-bank-muted font-mono text-[11px] tabular-nums">#{question.id}</span>
          <Badge value={question.status} />
          <span className="text-bank-muted font-mono text-[10px] tracking-[0.05em] uppercase">
            {isCanonical ? "canonical" : "variant"}
          </span>
        </div>
        <p className="question-text mt-1 line-clamp-2 text-[13px]">{question.question_text}</p>
        <p className="text-bank-muted mt-1 font-mono text-[11px] leading-relaxed">
          {isCanonical ? "the surviving version of this cluster" : decisionSummary(decision)}
        </p>
      </div>
      {clickable && <ChevronRight className="text-bank-muted size-4 shrink-0" aria-hidden="true" />}
    </li>
  );
}
